import { ref } from 'vue'
import { useAuthStore } from '@/store/auth'
import { useCommunicationStore } from '@/store/communication'
import { useI18n } from 'vue-i18n'

import { db, workspaces_version, lists_version } from '@/plugins/firebase'
import { useSegment } from '@/composables/analytics/useSegment'
import {
  collection,
  query,
  getDocs,
  where,
  doc,
  updateDoc,
  Timestamp,
  setDoc,
  arrayUnion,
  arrayRemove,
  writeBatch,
  deleteDoc
} from 'firebase/firestore'
import { log } from '@/plugins/logger'

export function useFirestoreWorkspaces() {
  // Define your reactive variables here
  const authStore = useAuthStore()
  const communicationStore = useCommunicationStore()
  const { trackWorkspaceMutated } = useSegment()
  const { t } = useI18n()

  const workspaces = ref([])
  const sharedWorkspaces = ref([])
  const workspace = ref({})
  const loadingWorkspaces = ref(false)
  const updatingWorkspace = ref(false)
  const workspacesRef = collection(
    db,
    `/${authStore.userData.company}/workspaces/${workspaces_version}`
  )

  // Define your functions here
  async function getUserOwnedWorkspaces() {
    loadingWorkspaces.value = true
    workspaces.value.splice(0)
    const q = query(
      workspacesRef,
      where('created_by', '==', authStore.userData.uid),
      where('archived', '==', false)
    )
    const querySnapshot = await getDocs(q)
    querySnapshot.forEach(doc => {
      workspaces.value.push(doc.data())
    })
    loadingWorkspaces.value = false
  }

  async function getSharedWorkspaces() {
    loadingWorkspaces.value = true
    sharedWorkspaces.value.splice(0)
    const q = query(
      workspacesRef,
      where('shared_users', 'array-contains', authStore.userData.uid),
      where('archived', '==', false)
    )
    const querySnapshot = await getDocs(q)
    querySnapshot.forEach(doc => {
      sharedWorkspaces.value.push(doc.data())
    })
    loadingWorkspaces.value = false
  }

  async function getWorkspaceById(workspaceId) {
    loadingWorkspaces.value = true
    const q = query(workspacesRef, where('id', '==', workspaceId))
    const querySnapshot = await getDocs(q)
    querySnapshot.forEach(doc => {
      workspace.value = doc.data()
    })
    loadingWorkspaces.value = false
  }

  async function updateWorkspaceItems() {
    // updates items in the list based on any changes to the list array (re-order, added, removed)
    let listDoc = doc(
      db,
      `/${authStore.userData.company}/workspaces/${workspaces_version}/${workspace.value.id}`
    )
    try {
      updatingWorkspace.value = true
      await updateDoc(listDoc, {
        items: workspace.value.items,
        updated: Timestamp.now(),
        updated_by: authStore.userData.uid
      })
      trackWorkspaceMutated(workspace.value.id, 'updated')
    } catch (error) {
      log.error('Error updating list items', error)
      communicationStore.setSnack({
        show: true,
        message: 'Error updating list items'
      })
    } finally {
      setTimeout(() => {
        // use timeout to make UI more smooth if update is very fast
        updatingWorkspace.value = false
      }, 250)
    }
  }

  async function addListToWorkspace(workspaceId, listId) {
    // add list to workspace items array
    let workspaceDoc = doc(
      db,
      `/${authStore.userData.company}/workspaces/${workspaces_version}/${workspaceId}`
    )
    try {
      updatingWorkspace.value = true
      await updateDoc(workspaceDoc, {
        items: arrayUnion(listId),
        updated: Timestamp.now(),
        updated_by: authStore.userData.uid
      })
      trackWorkspaceMutated(workspaceId, 'updated')
    } catch (error) {
      log.error('Error updating list items', error)
      communicationStore.setSnack({
        show: true,
        message: 'Error updating list items'
      })
    } finally {
      setTimeout(() => {
        // use timeout to make UI more smooth if update is very fast
        updatingWorkspace.value = false
      }, 250)
    }
  }

  async function removeListFromWorkspace(workspaceId, listId) {
    // remove list from workspace
    let workspaceDoc = doc(
      db,
      `/${authStore.userData.company}/workspaces/${workspaces_version}/${workspaceId}`
    )

    let success = false

    try {
      updatingWorkspace.value = true
      await updateDoc(workspaceDoc, {
        items: arrayRemove(listId),
        updated: Timestamp.now(),
        updated_by: authStore.userData.uid
      })
      trackWorkspaceMutated(workspaceId, 'updated')
      success = true
    } catch (error) {
      log.error('Error updating list items', error)
      communicationStore.setSnack({
        show: true,
        message: t('workspaces.errorMessages.updateWorkspace')
      })
    } finally {
      setTimeout(() => {
        // use timeout to make UI more smooth if update is very fast
        updatingWorkspace.value = false
      }, 250)
    }
    return success
  }

  async function createWorkspace(workspaceDetails) {
    let docRef = doc(workspacesRef)
    let newWorkspace = {
      ...workspaceDetails
    }

    newWorkspace['id'] = docRef.id
    newWorkspace['created'] = Timestamp.now()
    newWorkspace['created_by'] = authStore.userData.uid
    newWorkspace['updated'] = Timestamp.now()
    newWorkspace['updated_by'] = authStore.userData.uid
    newWorkspace['viewed'][authStore.userData.uid] = Timestamp.now()

    try {
      await setDoc(docRef, newWorkspace)

      communicationStore.setSnack({
        show: true,
        message: t('workspaces.successMessages.createWorkspace'),
        color: '',
        vertical: false,
        showCloseAction: true,
        timeout: 2000
      })
      trackWorkspaceMutated(newWorkspace.id, 'created')

      return newWorkspace // return workspace so it can be pushed to display arrays to match FS state.
    } catch (error) {
      log.error('Error adding document: ', error)
      communicationStore.setSnack({
        show: true,
        message: t('workspaces.errorMessages.createWorkspace'),
        color: '',
        vertical: false,
        showCloseAction: true,
        timeout: 2000
      })
      return false
    }
  }

  async function updateSharedUsers(workspaceId, userId, action) {
    // add or remove user from shared users array
    let workspaceDoc = doc(
      db,
      `/${authStore.userData.company}/workspaces/${workspaces_version}/${workspaceId}`
    )
    try {
      updatingWorkspace.value = true
      if (action === 'add') {
        await updateDoc(workspaceDoc, {
          shared_users: arrayUnion(userId),
          updated: Timestamp.now(),
          updated_by: authStore.userData.uid
        })
      } else if (action === 'remove') {
        await updateDoc(workspaceDoc, {
          shared_users: arrayRemove(userId),
          updated: Timestamp.now(),
          updated_by: authStore.userData.uid
        })
      }
      trackWorkspaceMutated(workspaceId, 'updated')
    } catch (error) {
      log.error('Error updating list items', error)
      communicationStore.setSnack({
        show: true,
        message: t('workspaces.errorMessages.updateWorkspace')
      })
    } finally {
      setTimeout(() => {
        // use timeout to make UI more smooth if update is very fast
        updatingWorkspace.value = false
      }, 250)
    }
  }

  async function refreshWorkspaces() {
    loadingWorkspaces.value = true
    workspaces.value.splice(0)
    sharedWorkspaces.value.splice(0)
    await getUserOwnedWorkspaces()
    await getSharedWorkspaces()
  }

  async function deleteWorkspace(workspace) {
    updatingWorkspace.value = true
    let success = false
    try {
      const workspaceDoc = doc(
        db,
        `/${authStore.userData.company}/workspaces/${workspaces_version}/${workspace.id}`
      )

      await batchDeleteListsById(workspace.items) // try to delete lists first, so if error deleting lists we can still interact with workspace
      await deleteDoc(workspaceDoc)
      trackWorkspaceMutated(workspace.id, 'deleted')
      success = true
    } catch (error) {
      log.error('Error deleting workspace', error)
      communicationStore.setSnack({
        show: true,
        message: t('workspaces.errorMessages.deleteWorkspace')
      })
    } finally {
      updatingWorkspace.value = false
    }
    return success
  }

  async function batchDeleteListsById(listDocIds) {
    let batch = writeBatch(db)
    try {
      listDocIds.forEach(listId => {
        let listDoc = doc(db, `/${authStore.userData.company}/lists/${lists_version}/${listId}`)
        batch.delete(listDoc)
      })
      await batch.commit()
    } catch (error) {
      log.error('Error deleting lists', error)
      throw new Error('Error deleting lists')
    }
  }

  async function logUserViewedWorkspace(userId, workspaceId) {
    let workspaceDoc = doc(
      db,
      `/${authStore.userData.company}/workspaces/${workspaces_version}/${workspaceId}`
    )
    try {
      const updateString = `viewed.${userId}`
      const updateObj = {
        [`${updateString}`]: Timestamp.now()
      }

      await updateDoc(workspaceDoc, updateObj)
    } catch (error) {
      log.error('Error logging user viewed workspace', error)
    }
  }

  async function updateWorkspaceName(workspaceId, newName) {
    let workspaceDoc = doc(
      db,
      `/${authStore.userData.company}/workspaces/${workspaces_version}/${workspaceId}`
    )
    trackWorkspaceMutated(workspaceId, 'updated')
    let success = false
    try {
      updatingWorkspace.value = true
      await updateDoc(workspaceDoc, {
        name: newName,
        updated: Timestamp.now(),
        updated_by: authStore.userData.uid
      })
      success = true
    } catch (error) {
      log.error('Error updating workspace name', error)
      communicationStore.setSnack({
        show: true,
        message: t('workspaces.errorMessages.updateWorkspace')
      })
    } finally {
      setTimeout(() => {
        // use timeout to make UI more smooth if update is very fast
        updatingWorkspace.value = false
      }, 250)
    }
    return success
  }

  // Return the reactive variables and functions as an object
  return {
    workspaces,
    loadingWorkspaces,
    getUserOwnedWorkspaces,
    workspace,
    getWorkspaceById,
    updatingWorkspace,
    updateWorkspaceItems,
    createWorkspace,
    sharedWorkspaces,
    getSharedWorkspaces,
    addListToWorkspace,
    removeListFromWorkspace,
    updateSharedUsers,
    refreshWorkspaces,
    logUserViewedWorkspace,
    updateWorkspaceName,
    deleteWorkspace,
    batchDeleteListsById
  }
}
